import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  TextField,
  Select,
  FormControl,
  MenuItem,
  Chip,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  Add,
  Edit,
  Visibility,
  Download,
  Refresh,
  CheckCircle,
  ErrorOutline,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import * as mammoth from "mammoth";
import api from "../../../api/axiosInterceptor";
import { SnackBarComponent } from "../../../Components/snackBar/snackBar";
import { LoadingSpinner } from "../../../Components/spinner/spinner";
import TableComponent from "../../../Components/TableComponent/TableComponent";
import { TODAY, StatusChip, ApprovalTimeline } from "./component/sharedForm";
import ApprovalPanel from "./approvalPanel";
import FEDropdown from "./feDropdown";

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE URLS — served via Django backend proxy
// GET /api/legal/templates/summons/
// GET /api/legal/templates/warrant/
// ─────────────────────────────────────────────────────────────────────────────
const TEMPLATE_URLS = {
  summons: "/legal/templates/summons/",
  warrant: "/legal/templates/warrant/",
};

// ─────────────────────────────────────────────────────────────────────────────
// PLACEHOLDER DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────
export const SUMMONS_PLACEHOLDERS = {
  sploca_no: { label: "SPLOCA No.", type: "text" },
  date: { label: "Date", type: "date" },
  fe: { label: "Financial Entity (M/s)", type: "fe-dropdown" },
  chief_promoter_name: { label: "Chief Promoter Name", type: "text" },
  crime_no: { label: "Crime No.", type: "text" },
  police_station: { label: "Police Station", type: "text" },
  section_act: { label: "Section & ACT", type: "text" },
  go_no: { label: "GO No. (E - ...)", type: "text" },
  bengaluru_dated: { label: "Bengaluru Dated", type: "date" },
  misc_no: { label: "MISC No.", type: "text" },
  misc_dated: { label: "MISC Dated", type: "date" },
  property_type: {
    label: "Property Type",
    type: "select",
    options: ["Immovable", "Movable", "Commercial", "Residential"],
  },
  appearance_date: { label: "Appearance Date", type: "date" },
  appearance_time: { label: "Appearance Time", type: "time" },
  records_to_produce: { label: "Records to Produce", type: "textarea" },
  given_under_date: { label: "Given Under Date", type: "date" },
  to_address: { label: "To Address", type: "textarea" },
};

export const WARRANT_PLACEHOLDERS = {
  sploca_no: { label: "SPLOCA No.", type: "text" },
  date: { label: "Date", type: "date" },
  fe: { label: "Financial Entity (M/s)", type: "fe-dropdown" },
  summons_dated: { label: "Summons Dated", type: "date" },
  appearance_on_date: { label: "Appearance On Date", type: "date" },
  issued_to_name: { label: "Issued To Name", type: "text" },
  hq_at: { label: "HQ At", type: "text" },
  letter_dated: { label: "Letter Dated", type: "date" },
  failure_summons_dated: { label: "Failure Summons Dated", type: "date" },
  produce_at_place: { label: "Produce At Place", type: "text" },
  produce_on_date: { label: "Produce On Date", type: "date" },
  bail_amount: { label: "Bail Amount (Rs.)", type: "number" },
  given_under_date: { label: "Given Under Date", type: "date" },
  to_address: { label: "To Address", type: "textarea" },
};

// ─────────────────────────────────────────────────────────────────────────────
// BLANK DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────
const buildBlank = (map) =>
  Object.fromEntries(
    Object.keys(map).map((k) => [k, k === "date" ? TODAY : ""]),
  );
const SUMMONS_BLANK = buildBlank(SUMMONS_PLACEHOLDERS);
const WARRANT_BLANK = buildBlank(WARRANT_PLACEHOLDERS);

// ─────────────────────────────────────────────────────────────────────────────
// STATUS
// ─────────────────────────────────────────────────────────────────────────────
const SL = {
  draft: "Draft",
  submitted: "Submitted",
  legal_review: "Legal Review",
  approved_l1: "Approved (Legal)",
  rejected_l1: "Rejected (Legal)",
  approved_ac: "Approved (AC)",
  rejected_ac: "Rejected (AC)",
  approved_js: "Approved (JS)",
  rejected_js: "Rejected (JS)",
  approved_ca: "Approved (CA/SPLOCA)",
  rejected_ca: "Rejected (CA/SPLOCA)",
  issued: "Issued",
};
const EDITABLE = [
  "draft",
  "rejected_l1",
  "rejected_ac",
  "rejected_js",
  "rejected_ca",
];
const CAN_CREATE = ["super_staff"];

// ─────────────────────────────────────────────────────────────────────────────
// MAMMOTH OPTIONS — preserves Word paragraph spacing, alignment, headings,
// tables, list structure, images, and tab stops faithfully.
// ─────────────────────────────────────────────────────────────────────────────
const MAMMOTH_OPTIONS = {
  styleMap: [
    "p[style-name='Heading 1'] => h1:fresh",
    "p[style-name='Heading 2'] => h2:fresh",
    "p[style-name='Heading 3'] => h3:fresh",
    "p[style-name='Heading 4'] => h4:fresh",
    "p[style-name='Normal'] => p:fresh",
    "p[style-name='Body Text'] => p:fresh",
    "p[style-name='Body Text 2'] => p:fresh",
    "p[style-name='centered'] => p.doc-centered:fresh",
    "p[style-name='Center'] => p.doc-centered:fresh",
    "p[style-name='List Paragraph'] => p.doc-list-para:fresh",
    "p[style-name='List Number'] => ol > li:fresh",
    "p[style-name='List Bullet'] => ul > li:fresh",
    "p[style-name='Caption'] => p.doc-caption:fresh",
    "r[style-name='Strong'] => strong",
    "r[style-name='Emphasis'] => em",
    "r[style-name='Underline'] => u",
    "t => span.doc-tab",
  ],
  includeDefaultStyleMap: true,
  ignoreEmptyParagraphs: false,
  convertImage: mammoth.images.imgElement((image) =>
    image.read("base64").then((imageBuffer) => ({
      src: `data:${image.contentType};base64,${imageBuffer}`,
    })),
  ),
};

// ─────────────────────────────────────────────────────────────────────────────
// CSS — Word-faithful preview styles (A4 page, Times New Roman, justified)
// ─────────────────────────────────────────────────────────────────────────────
const PREVIEW_STYLES = `
  .word-preview-root {
    font-family: 'Times New Roman', Times, serif;
    font-size: 12pt;
    line-height: 1.5;
    color: #000;
    background: #fff;
    padding: 72pt 90pt;
    max-width: 816px;
    margin: 0 auto;
    box-shadow: 0 0 0 1px #ddd;
    min-height: 1123px;
    text-align: justify;
  }
  .word-preview-root h1,
  .word-preview-root h2,
  .word-preview-root h3,
  .word-preview-root h4 {
    text-align: center;
    font-family: 'Times New Roman', Times, serif;
    font-weight: bold;
    margin: 0 0 6pt 0;
    line-height: 1.4;
  }
  .word-preview-root h1 { font-size: 14pt; }
  .word-preview-root h2 { font-size: 13pt; }
  .word-preview-root h3 { font-size: 12pt; }
  .word-preview-root p {
    margin: 0 0 6pt 0;
    text-align: justify;
  }
  .word-preview-root p:empty::after,
  .word-preview-root p br:only-child {
    content: '\\00a0';
    display: block;
  }
  .word-preview-root p.doc-centered { text-align: center; }
  .word-preview-root p.doc-list-para { margin-left: 36pt; }
  .word-preview-root ol,
  .word-preview-root ul { margin: 0 0 6pt 36pt; padding: 0; }
  .word-preview-root li { margin-bottom: 3pt; }
  .word-preview-root p.doc-caption { text-align: center; font-size: 10pt; color: #444; }
  .word-preview-root span.doc-tab { display: inline-block; width: 36pt; }
  .word-preview-root table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 6pt;
    font-size: 11pt;
  }
  .word-preview-root td,
  .word-preview-root th {
    border: 1px solid #000;
    padding: 4pt 6pt;
    vertical-align: top;
  }
  .word-preview-root th { font-weight: bold; background: #f2f2f2; }
  .word-preview-root strong { font-weight: bold; }
  .word-preview-root em     { font-style: italic; }
  .word-preview-root u      { text-decoration: underline; }
  .word-preview-root .ph-filled {
    display: inline-block;
    background: #e8f5e9;
    border: 1px solid #66bb6a;
    border-radius: 3px;
    padding: 0 6px;
    font-size: 11px;
    color: #1b5e20;
    font-weight: 700;
    vertical-align: middle;
    font-family: Arial, sans-serif;
    line-height: 1.6;
  }
  .word-preview-root .ph-empty {
    display: inline-block;
    background: #fff3e0;
    border: 1px dashed #ff9800;
    border-radius: 3px;
    padding: 0 6px;
    font-size: 11px;
    color: #e65100;
    vertical-align: middle;
    font-family: Arial, sans-serif;
    line-height: 1.6;
  }
  .word-preview-root .ph-missing {
    color: #bbb;
    font-style: italic;
    font-family: Arial, sans-serif;
    font-size: 11px;
  }
  @media print {
    .word-preview-root { box-shadow: none; padding: 0; max-width: 100%; }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// HOOK — fetch raw .docx ArrayBuffer, convert to HTML with mammoth,
//         keep rawBuffer so we can inject values and download for print.
// ─────────────────────────────────────────────────────────────────────────────
const useFetchTemplate = (proxyUrl) => {
  const [templateHtml, setTemplateHtml] = useState(null);
  const [rawBuffer, setRawBuffer] = useState(null);
  const [detectedKeys, setDetectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!proxyUrl) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTemplateHtml(null);
    setRawBuffer(null);
    setDetectedKeys([]);

    (async () => {
      try {
        const res = await api.get(proxyUrl, { responseType: "arraybuffer" });
        const arrayBuffer = res.data;
        const result = await mammoth.convertToHtml(
          { arrayBuffer },
          MAMMOTH_OPTIONS,
        );
        const html = result.value;
        const keys = [
          ...new Set([...html.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1])),
        ];
        if (!cancelled) {
          setTemplateHtml(html);
          setRawBuffer(arrayBuffer);
          setDetectedKeys(keys);
        }
      } catch (e) {
        if (!cancelled)
          setError(
            e?.response?.data?.error ||
              e?.message ||
              "Unknown error loading template",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [proxyUrl]);

  return { templateHtml, rawBuffer, detectedKeys, loading, error };
};

// ─────────────────────────────────────────────────────────────────────────────
// XML ESCAPE
// ─────────────────────────────────────────────────────────────────────────────
const escapeXml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

// ─────────────────────────────────────────────────────────────────────────────
// COLLAPSE SPLIT RUNS & REPLACE PLACEHOLDERS
// ─────────────────────────────────────────────────────────────────────────────
const collapseAndReplace = (xml, substitutions) =>
  xml.replace(/<w:p[ >][\s\S]*?<\/w:p>/g, (para) => {
    const runPattern = /<w:r[ >][\s\S]*?<\/w:r>/g;
    const runs = [];
    let m;
    while ((m = runPattern.exec(para)) !== null) {
      const tMatch = m[0].match(/<w:t(?:[^>]*)>([\s\S]*?)<\/w:t>/);
      runs.push({ xml: m[0], text: tMatch ? tMatch[1] : "", index: m.index });
    }
    if (!runs.length) return para;

    const fullText = runs.map((r) => r.text).join("");
    const hasMatch = Object.keys(substitutions).some((k) =>
      fullText.includes(`{{${k}}}`),
    );
    if (!hasMatch) return para;

    let substituted = fullText;
    for (const [key, val] of Object.entries(substitutions)) {
      substituted = substituted.split(`{{${key}}}`).join(escapeXml(val));
    }

    const firstRun = runs[0];
    const rPrMatch = firstRun.xml.match(/<w:rPr>[\s\S]*?<\/w:rPr>/);
    const rPr = rPrMatch ? rPrMatch[0] : "";
    const mergedRun = `<w:r>${rPr}<w:t xml:space="preserve">${substituted}</w:t></w:r>`;
    const firstStart = para.indexOf(firstRun.xml);
    const lastRun = runs[runs.length - 1];
    const lastEnd = para.indexOf(lastRun.xml) + lastRun.xml.length;

    return para.slice(0, firstStart) + mergedRun + para.slice(lastEnd);
  });

// ─────────────────────────────────────────────────────────────────────────────
// INJECT FILLED VALUES INTO DOCX BYTES
// ─────────────────────────────────────────────────────────────────────────────
const injectIntoDocx = async (arrayBuffer, formData, placeholderMap) => {
  try {
    const JSZip = (await import("jszip")).default;
    const zip = await JSZip.loadAsync(arrayBuffer);

    const substitutions = {};
    Object.keys(placeholderMap).forEach((key) => {
      substitutions[key] =
        key === "fe" ? formData._fe_display || "" : formData[key] || "";
    });

    const filesToPatch = [
      "word/document.xml",
      ...Object.keys(zip.files).filter((f) =>
        /^word\/(header|footer)\d*\.xml$/.test(f),
      ),
    ];

    for (const fileName of filesToPatch) {
      const file = zip.file(fileName);
      if (!file) continue;

      let xml = await file.async("string");
      xml = collapseAndReplace(xml, substitutions);
      Object.entries(substitutions).forEach(([key, val]) => {
        xml = xml.split(`{{${key}}}`).join(escapeXml(val));
      });

      zip.file(fileName, xml);
    }

    return await zip.generateAsync({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    });
  } catch (err) {
    console.error("injectIntoDocx failed:", err);
    return new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// DOWNLOAD FILLED DOCX
// ─────────────────────────────────────────────────────────────────────────────
const downloadFilledDocx = async (
  rawBuffer,
  formData,
  placeholderMap,
  filename,
) => {
  const blob = await injectIntoDocx(rawBuffer, formData, placeholderMap);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE PREVIEW
// ─────────────────────────────────────────────────────────────────────────────
const TemplatePreview = ({ html, formData, readOnly }) => {
  const rendered = html.replace(/\{\{(\w+)\}\}/g, (_m, key) => {
    const val = key === "fe" ? formData._fe_display || "" : formData[key] || "";
    if (!val) {
      return readOnly
        ? `<span class="ph-missing">[${key}]</span>`
        : `<span class="ph-empty">{{${key}}}</span>`;
    }
    return `<span class="ph-filled">${val}</span>`;
  });

  return (
    <>
      <style>{PREVIEW_STYLES}</style>
      <div
        className="word-preview-root"
        dangerouslySetInnerHTML={{ __html: rendered }}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE ERROR
// ─────────────────────────────────────────────────────────────────────────────
const TemplateError = ({ error, templateKey }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      p: 6,
      gap: 2,
      background: "#fff9f9",
      border: "1px dashed #f44336",
      borderRadius: 2,
      minHeight: 300,
    }}
  >
    <ErrorOutline sx={{ fontSize: 48, color: "error.main" }} />
    <Typography variant="h6" color="error.main" fontWeight="bold">
      Template Failed to Load
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ textAlign: "center", maxWidth: 420 }}
    >
      {error}
    </Typography>
    <Alert severity="info" sx={{ mt: 1, width: "100%", maxWidth: 480 }}>
      <Typography variant="body2">
        Backend proxy endpoint:{" "}
        <code>{TEMPLATE_URLS[templateKey]}</code>
        <br />
        Ensure the Django view is registered and the S3 file exists.
      </Typography>
    </Alert>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE LOADING
// ─────────────────────────────────────────────────────────────────────────────
const TemplateLoading = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      p: 6,
      gap: 2,
      minHeight: 300,
    }}
  >
    <CircularProgress size={32} />
    <Typography variant="body2" color="text.secondary">
      Loading Word template…
    </Typography>
    <LinearProgress sx={{ width: 200, mt: 1 }} />
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// PLACEHOLDER FIELD
// ─────────────────────────────────────────────────────────────────────────────
const PlaceholderField = ({
  fieldKey,
  config,
  formData,
  onChange,
  onFESelect,
}) => {
  const value = formData[fieldKey] || "";
  const isFilled = fieldKey === "fe" ? !!formData._fe_display : !!value;

  const label = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
      <code
        style={{
          background: "#f5f5f5",
          padding: "1px 5px",
          borderRadius: 3,
          fontSize: 11,
        }}
      >
        {`{{${fieldKey}}}`}
      </code>
      <Typography variant="caption" color="text.secondary">
        — {config.label}
      </Typography>
      {isFilled && <CheckCircle sx={{ fontSize: 13, color: "success.main" }} />}
    </Box>
  );

  if (config.type === "fe-dropdown") {
    return (
      <Box sx={{ mb: 2 }}>
        {label}
        <FEDropdown
          value={formData.fe}
          disabled={false}
          showAddress={true}
          onSelect={(fe) => onFESelect(fe)}
        />
        {formData._fe_display && (
          <Typography
            variant="caption"
            sx={{ color: "success.dark", mt: 0.4, display: "block" }}
          >
            ✓ <strong>{formData._fe_display}</strong>
          </Typography>
        )}
      </Box>
    );
  }

  if (config.type === "select") {
    return (
      <Box sx={{ mb: 2 }}>
        {label}
        <FormControl fullWidth size="small">
          <Select
            value={value}
            displayEmpty
            onChange={(e) => onChange(fieldKey, e.target.value)}
            sx={{ fontSize: 13 }}
          >
            <MenuItem value="">
              <em>Select…</em>
            </MenuItem>
            {(config.options || []).map((o) => (
              <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  if (config.type === "textarea") {
    return (
      <Box sx={{ mb: 2 }}>
        {label}
        <TextField
          value={value}
          onChange={(e) => onChange(fieldKey, e.target.value)}
          multiline
          rows={2}
          fullWidth
          size="small"
          InputProps={{ style: { fontSize: 13 } }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {label}
      <TextField
        type={config.type || "text"}
        value={value}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        fullWidth
        size="small"
        InputProps={{ style: { fontSize: 13 } }}
        InputLabelProps={
          ["date", "time"].includes(config.type) ? { shrink: true } : {}
        }
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FIELDS PANEL — sticky left column
// ─────────────────────────────────────────────────────────────────────────────
const FieldsPanel = ({
  isSummons,
  formData,
  onChange,
  onFESelect,
  detectedKeys,
}) => {
  const knownMap = isSummons ? SUMMONS_PLACEHOLDERS : WARRANT_PLACEHOLDERS;
  const keysToRender = detectedKeys?.length
    ? detectedKeys
    : Object.keys(knownMap);
  const filledCount = keysToRender.filter((k) =>
    k === "fe" ? !!formData._fe_display : !!formData[k],
  ).length;

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        p: 2,
        position: "sticky",
        top: 0,
        maxHeight: "calc(100vh - 180px)",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Placeholders
        </Typography>
        <Chip
          size="small"
          label={`${filledCount} / ${keysToRender.length} filled`}
          color={filledCount === keysToRender.length ? "success" : "default"}
          sx={{ fontSize: 11 }}
        />
      </Box>
      {keysToRender.map((key) => {
        const config = knownMap[key] || { label: key, type: "text" };
        return (
          <PlaceholderField
            key={key}
            fieldKey={key}
            config={config}
            formData={formData}
            onChange={onChange}
            onFESelect={onFESelect}
          />
        );
      })}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED BASE COMPONENT — receives all type-specific config as props
// ─────────────────────────────────────────────────────────────────────────────
const BaseDocumentComponent = ({
  isSummons,
  ep,
  blank,
  placeholderMap,
  templateKey,
  formTitle,
  formSub,
  tableCols,
}) => {
  const role = useSelector((s) => s.auth.role);

  const [view, setView] = useState("list");
  const [list, setList] = useState([]);
  const [listLoading, setLL] = useState(false);
  const [formData, setFormData] = useState({});
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  // ── FE filter — only used when isSummons is true
  const [filterFE, setFilterFE] = useState(null);

  const activeTemplate = useFetchTemplate(TEMPLATE_URLS[templateKey]);

  // ── Fetch list
  const fetchList = useCallback(async () => {
    setLL(true);
    try {
      const res = await api.get(ep);
      setList(res.data);
    } catch {
      setSnack({
        open: true,
        message: "Failed to load list",
        severity: "error",
      });
    } finally {
      setLL(false);
    }
  }, [ep]);

  useEffect(() => {
    fetchList();
    setView("list");
  }, []);

  // ── Field helpers
  const set = (f, v) => setFormData((d) => ({ ...d, [f]: v }));

  const handleFESelect = (fe) => {
    if (!fe) return;
    setFormData((d) => ({
      ...d,
      fe: fe.id,
      _fe_display: fe.name_of_fe,
      fe_detail: fe,
    }));
  };

  // ── Navigation
  const openNew = () => {
    setFormData({ ...blank });
    setSelected(null);
    setView("form");
  };

  const openEdit = (item) => {
    setFormData({ ...item, _fe_display: item.fe_detail?.name_of_fe || "" });
    setSelected(item);
    setView("form");
  };

  const openView = async (item) => {
    try {
      const res = await api.get(`${ep}${item.id}/`);
      setFormData({
        ...res.data,
        _fe_display: res.data.fe_detail?.name_of_fe || "",
      });
      setSelected(res.data);
    } catch {
      setFormData({ ...item, _fe_display: item.fe_detail?.name_of_fe || "" });
      setSelected(item);
    }
    setView("detail");
  };

  // ── Save
  const handleSave = async () => {
    setSaving(true);
    const { fe_detail, _fe_display, ...payload } = formData;
    try {
      if (selected) {
        await api.patch(`${ep}${selected.id}/`, payload);
        setSnack({
          open: true,
          message: "Updated successfully",
          severity: "success",
        });
      } else {
        await api.post(ep, payload);
        setSnack({
          open: true,
          message: "Created successfully",
          severity: "success",
        });
      }
      fetchList();
      setView("list");
    } catch (e) {
      setSnack({
        open: true,
        message: JSON.stringify(e.response?.data) || "Save failed",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Download filled .docx
  const handlePrint = async () => {
    if (!activeTemplate.rawBuffer) {
      setSnack({
        open: true,
        message: "Template not loaded yet",
        severity: "warning",
      });
      return;
    }
    setPrinting(true);
    try {
      const docType = isSummons ? "summons" : "warrant";
      const sploca = formData.sploca_no || "draft";
      await downloadFilledDocx(
        activeTemplate.rawBuffer,
        formData,
        placeholderMap,
        `${docType}_${sploca}.docx`,
      );
      setSnack({
        open: true,
        message:
          "Filled .docx downloaded — open in Word and use File → Print for exact formatting.",
        severity: "info",
      });
    } catch (e) {
      setSnack({
        open: true,
        message: `Error: ${e.message}`,
        severity: "error",
      });
    } finally {
      setPrinting(false);
    }
  };

  // ── Action cell
  const actionCell = (p) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Tooltip title="View">
        <IconButton size="small" onClick={() => openView(p.row)}>
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>
      {EDITABLE.includes(p.row.status) && CAN_CREATE.includes(role) && (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            color="primary"
            onClick={() => openEdit(p.row)}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const tableRows = list.map((item, i) => ({
    ...item,
    slNo: i + 1,
    statusLabel: SL[item.status] || item.status,
    fe_name: item.fe_detail?.name_of_fe || "",
  }));

  // ── For summons: filter by selected FE; for warrant: show all rows
  const displayRows = isSummons
    ? filterFE
      ? tableRows.filter(
          (r) => r.fe === filterFE.id || r.fe_detail?.id === filterFE.id,
        )
      : []
    : tableRows;

  const columns = tableCols(actionCell);

  // ── Document preview helper
  const renderDocPreview = (readOnly = false) => {
    if (activeTemplate.loading) return <TemplateLoading />;
    if (activeTemplate.error)
      return (
        <TemplateError error={activeTemplate.error} templateKey={templateKey} />
      );
    if (activeTemplate.templateHtml)
      return (
        <TemplatePreview
          html={activeTemplate.templateHtml}
          formData={formData}
          readOnly={readOnly}
        />
      );
    return null;
  };

  // ── Print/Download button
  const PrintButton = () => (
    <Tooltip title="Downloads a filled .docx — open in Microsoft Word and print for exact formatting">
      <span>
        <Button
          variant="outlined"
          startIcon={printing ? <CircularProgress size={16} /> : <Download />}
          onClick={handlePrint}
          disabled={printing || !activeTemplate.rawBuffer}
        >
          {printing ? "Preparing…" : "Download & Print (Word)"}
        </Button>
      </span>
    </Tooltip>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        borderRadius: 2,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        backgroundColor: "#fff",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          background: "rgba(25,118,210,0.04)",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {formTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Flow: Case Worker → Shirastedar → Legal → AC → JS → CA / SPLOCA
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* ── LIST ──────────────────────────────────────────────────────────── */}
        {view === "list" && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  {isSummons ? "Summons List" : "Warrant List"}
                </Typography>
                {activeTemplate.loading && (
                  <Chip
                    size="small"
                    label="Loading template…"
                    variant="outlined"
                    sx={{ fontSize: 11 }}
                  />
                )}
                {activeTemplate.templateHtml && !activeTemplate.loading && (
                  <Chip
                    size="small"
                    label="Template ready"
                    color="success"
                    sx={{ fontSize: 11 }}
                  />
                )}
                {activeTemplate.error && (
                  <Chip
                    size="small"
                    icon={<ErrorOutline sx={{ fontSize: 14 }} />}
                    label="Template error"
                    color="error"
                    sx={{ fontSize: 11 }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh list">
                  <IconButton onClick={fetchList}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
                {CAN_CREATE.includes(role) && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openNew}
                  >
                    New
                  </Button>
                )}
              </Box>
            </Box>

            {/* FE Filter — summons only */}
            {isSummons && (
              <Box sx={{ mb: 2, maxWidth: 400 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Select Financial Entity to view records
                </Typography>
                <FEDropdown
                  value={filterFE?.id || null}
                  disabled={false}
                  showAddress={true}
                  onSelect={(fe) => setFilterFE(fe || null)}
                />
              </Box>
            )}

            {/* Table or prompt */}
            {isSummons && !filterFE ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: "1px dashed #bbb",
                  borderRadius: 2,
                  background: "#fafafa",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Please select a Financial Entity above to view records.
                </Typography>
              </Box>
            ) : listLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <LoadingSpinner size={30} />
              </Box>
            ) : (
              <TableComponent
                columns={columns}
                rows={displayRows}
                manageColumnStatus
                enableSort
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    fontWeight: "bold",
                  },
                }}
              />
            )}
          </>
        )}

        {/* ── FORM ──────────────────────────────────────────────────────────── */}
        {view === "form" && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
                alignItems: "center",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                {formTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formSub}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "360px 1fr",
                gap: 2,
                alignItems: "start",
              }}
            >
              {/* LEFT — placeholder inputs */}
              {activeTemplate.templateHtml && (
                <FieldsPanel
                  isSummons={isSummons}
                  formData={formData}
                  onChange={set}
                  onFESelect={handleFESelect}
                  detectedKeys={activeTemplate.detectedKeys}
                />
              )}

              {/* RIGHT — Word-faithful live preview */}
              <Box
                sx={{
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  p: 2,
                  minHeight: 600,
                  overflowX: "auto",
                  gridColumn: activeTemplate.templateHtml ? "auto" : "1 / -1",
                }}
              >
                {activeTemplate.templateHtml && (
                  <Box
                    sx={{
                      mb: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="bold"
                    >
                      Live Preview (Word-faithful)
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        background: "#e8f5e9",
                        border: "1px solid #66bb6a",
                        borderRadius: "3px",
                        px: 0.5,
                        fontSize: 11,
                        color: "#1b5e20",
                      }}
                    >
                      filled
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      /
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        background: "#fff3e0",
                        border: "1px dashed #ff9800",
                        borderRadius: "3px",
                        px: 0.5,
                        fontSize: 11,
                        color: "#e65100",
                      }}
                    >
                      {"{{empty}}"}
                    </Box>
                  </Box>
                )}
                {renderDocPreview(false)}
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, mt: 3, alignItems: "center" }}>
              <Button variant="outlined" onClick={() => setView("list")}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !activeTemplate.templateHtml}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                {selected ? "Update" : "Save Draft"}
              </Button>
              <PrintButton />
              {!activeTemplate.templateHtml && !activeTemplate.loading && (
                <Typography variant="caption" color="error">
                  Template must load successfully before saving.
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* ── DETAIL ────────────────────────────────────────────────────────── */}
        {view === "detail" && selected && (
          <>
            <Button size="small" onClick={() => setView("list")} sx={{ mb: 1 }}>
              ← Back
            </Button>

            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {formTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formSub}
              </Typography>
              {/* <Box sx={{ mt: 1 }}>
                <StatusChip status={selected.status} />
              </Box> */}
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                background: "#f0f0f0",
                border: "1px solid #ddd",
                borderRadius: 2,
                p: 2,
                mb: 2,
                overflowX: "auto",
              }}
            >
              {renderDocPreview(true)}
            </Box>

            <ApprovalTimeline form={selected} flowType="legal" />
            <ApprovalPanel
              form={selected}
              apiPath={ep}
              role={role}
              flowType="legal"
              onRefresh={() => openView(selected)}
            />

            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button variant="outlined" onClick={() => setView("list")}>
                Back to List
              </Button>
              <PrintButton />
            </Box>
          </>
        )}
      </Box>

      <SnackBarComponent
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUMMONS COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export const SummonsComponent = () => {
  const summonsTableCols = (actionCell) => [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    { id: "sploca_no", field: "sploca_no", headerName: "SPLOCA No.", width: 130 },
    { id: "date", field: "date", headerName: "Date", width: 120 },
    { id: "fe_name", field: "fe_name", headerName: "Financial Entity", width: 240 },
    { id: "chief_promoter_name", field: "chief_promoter_name", headerName: "Chief Promoter", width: 180 },
    { id: "appearance_date", field: "appearance_date", headerName: "Appearance Date", width: 150 },
    { id: "statusLabel", field: "statusLabel", headerName: "Status", width: 180 },
    { id: "actions", field: "actions", headerName: "Actions", width: 110, renderCell: actionCell },
  ];

  return (
    <BaseDocumentComponent
      isSummons={true}
      ep="/legal/summons/"
      blank={SUMMONS_BLANK}
      placeholderMap={SUMMONS_PLACEHOLDERS}
      templateKey="summons"
      formTitle="SUMMONS FOR APPEARANCE AND PRODUCTION OF DOCUMENTS"
      formSub="UNDER SECTION 3(1a) READ WITH SECTION 6(2)(c) OF KPIDFE ACT 2004"
      tableCols={summonsTableCols}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WARRANT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export const WarrantComponent = () => {
  const warrantTableCols = (actionCell) => [
    { id: "slNo", field: "slNo", headerName: "Sl No.", width: 70 },
    { id: "sploca_no", field: "sploca_no", headerName: "SPLOCA No.", width: 130 },
    { id: "date", field: "date", headerName: "Date", width: 120 },
    { id: "issued_to_name", field: "issued_to_name", headerName: "Issued To", width: 220 },
    { id: "summons_dated", field: "summons_dated", headerName: "Original Summons", width: 160 },
    { id: "produce_on_date", field: "produce_on_date", headerName: "Produce On", width: 140 },
    { id: "bail_amount", field: "bail_amount", headerName: "Bail (Rs.)", width: 120 },
    { id: "statusLabel", field: "statusLabel", headerName: "Status", width: 180 },
    { id: "actions", field: "actions", headerName: "Actions", width: 110, renderCell: actionCell },
  ];

  return (
    <BaseDocumentComponent
      isSummons={false}
      ep="/legal/warrant/"
      blank={WARRANT_BLANK}
      placeholderMap={WARRANT_PLACEHOLDERS}
      templateKey="warrant"
      formTitle="WARRANT OF ARREST (Bailable)"
      formSub="SECTION 3(1a) OF KPIDFE ACT 2004 READ WITH ORDER 16 RULE 10 OF THE CPC"
      tableCols={warrantTableCols}
    />
  );
};

